"use client";

import { useEffect, useState } from "react";
import { Eye, X } from "lucide-react";

type ImpersonatedUser = {
  id: string;
  email: string;
  name: string | null;
  role: string;
};

export function ImpersonationBanner() {
  const [user, setUser] = useState<ImpersonatedUser | null>(null);
  const [exiting, setExiting] = useState(false);

  useEffect(() => {
    // Read from cookie on mount
    const match = document.cookie.match(/impersonate-user=([^;]+)/);
    if (match) {
      try {
        setUser(JSON.parse(decodeURIComponent(match[1])));
      } catch {
        // Invalid cookie — ignore
      }
    }

    // Also check server state
    fetch("/api/admin/impersonate")
      .then((r) => r.json())
      .then((data: { impersonating: ImpersonatedUser | null }) => {
        if (data.impersonating) {
          setUser(data.impersonating);
        }
      })
      .catch(() => {});
  }, []);

  async function handleExit() {
    setExiting(true);
    try {
      await fetch("/api/admin/impersonate", { method: "DELETE" });
      document.cookie = "impersonate-user=;path=/;max-age=0";
      window.location.reload();
    } catch {
      setExiting(false);
    }
  }

  if (!user) return null;

  const ROLE_LABELS: Record<string, string> = {
    admin: "Admin",
    telemarketer: "Telemarketer",
    advisor: "Advisor",
    bsc: "BSC",
  };

  return (
    <div className="flex items-center justify-between bg-amber-500 px-4 py-2 text-sm font-medium text-amber-950">
      <div className="flex items-center gap-2">
        <Eye className="h-4 w-4" />
        <span>
          Viewing as: <strong>{user.name ?? user.email}</strong> ({ROLE_LABELS[user.role] ?? user.role})
        </span>
      </div>
      <button
        onClick={handleExit}
        disabled={exiting}
        className="flex items-center gap-1 rounded-md bg-amber-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
      >
        <X className="h-3 w-3" />
        {exiting ? "Exiting..." : "Exit"}
      </button>
    </div>
  );
}
